import { create, awaitRedraw } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { eventHandler, locate, locateNode, locateNodeByClassName } from "@dhx/ts-common/html";
import {
	extend,
	findIndex,
	getMaxArrayNymber,
	getMinArrayNymber,
	isEmptyObj,
	isDefined,
} from "@dhx/ts-common/core";
import { IKeyManager, KeyManager } from "@dhx/ts-common/KeyManager";
import { Id, ITouchParam } from "@dhx/ts-common/types";
import { View } from "@dhx/ts-common/view";
import {
	DataCollection,
	DataEvents,
	dhxWarning,
	DragEvents,
	dragManager,
	IDataCollection,
	IDataEventsHandlersMap,
	IDataItem,
	IDragEventsHandlersMap,
	isTreeCollection,
} from "@dhx/ts-data";
import { Exporter } from "./Exporter";
import { countColumns, getMaxColsWidth, getUnique, normalizeColumns, toFormat } from "./helpers/data";
import { getWidth, getHeight } from "./helpers/cells";
import {
	isRowEmpty,
	isSortable,
	getTotalWidth,
	isTooltip,
	isHtmlEnable,
	getTotalHeight,
} from "./helpers/main";
import { Selection } from "./Selection";
import {
	Dirs,
	EditorType,
	GridEvents,
	IAdjustBy,
	ICellRect,
	ICol,
	IContentList,
	ICoords,
	IEventHandlersMap,
	IGrid,
	IGridConfig,
	IRow,
	IScrollState,
	ISelection,
	ISpan,
	GridSystemEvents,
	ISystemEventHandlersMap,
	IColumnsWidth,
	ISortingState,
	SortFunction,
} from "./types";
import { render } from "./ui/render";
import { stringToDate } from "@dhx/ts-common/date";
import { getContent } from "./ui/content";
import { startResize } from "./columnsResizer";
import { tooltip } from "@dhx/ts-message";
import { getKeysHandlers } from "./helpers/keys";
import { focusManager } from "@dhx/ts-common/FocusManager";
import { Combobox } from "@dhx/ts-combobox";
import { normalizeSpan } from "./ui/Cells";

export class Grid extends View implements IGrid {
	public data: IDataCollection;
	public config: IGridConfig;
	public events: IEventSystem<
		DataEvents | GridEvents | DragEvents,
		IEventHandlersMap & IDataEventsHandlersMap & IDragEventsHandlersMap
	>;
	public export: Exporter;
	public content: IContentList;
	public selection: ISelection;
	public keyManager: IKeyManager;

	protected _touch: ITouchParam = {
		duration: 350,
		dblDuration: 300,
		timer: null,
		start: false,
		timeStamp: null,
	};

	protected _scroll: IScrollState;
	protected _events: IEventSystem<GridSystemEvents, ISystemEventHandlersMap>;
	protected _htmlEvents: any;
	private _sortDir: Dirs;
	private _sortBy: Id;
	private _filterData: any[];
	protected _activeFilters: {};
	private _hiddenFilters: {};

	constructor(container: HTMLElement | string, config?: IGridConfig) {
		super(container, config);
		this.config = extend(
			({
				rowHeight: 40,
				headerRowHeight: 40,
				footerRowHeight: 40,
				keyNavigation: true,
				// selection: "cell", // TODO: turn it on to test aria attrs only OR fix keynavigation to work without selection (or special selection mode enabled by default)
				sortable: true,
				columns: [],
				data: [],
				tooltip: true,
				rootParent: (typeof container === "string" && container) || this._uid,
				// TODO: remove suite_7.0
				headerSort: true,
			} as any) as IGridConfig,
			config
		);

		this.content = getContent();
		this._scroll = {
			top: 0,
			left: 0,
		};
		const normalizeSpans = () => {
			this.config.spans = this.config.spans?.map(span => normalizeSpan(span, this.config));
		};
		if (this.config.data instanceof DataCollection) {
			awaitRedraw().then(() => normalizeSpans());
		} else {
			normalizeSpans();
		}

		// TODO: remove suite_7.0
		this.config.autoWidth = this.config.autoWidth || this.config.fitToContainer;
		this.config.adjust = this.config.adjust || this.config.columnsAutoWidth;
		this.config.editable = this.config.editable || this.config.editing;
		this.config.leftSplit = this.config.leftSplit || this.config.splitAt;
		if (!this.config.sortable || !this.config.headerSort) {
			this.config.sortable = false;
		}
		this.config.columns.forEach(col => {
			col.format = col.format || col.dateFormat;
		});

		const showCellTooltip = (row, column, node) => {
			if (row && column && isTooltip(this.config, column)) {
				let value = toFormat(row[column.id], column.type, column.format);
				const checkIsExistValue = () => !!value || typeof value === "boolean";
				if (column.tooltipTemplate) {
					value = column.tooltipTemplate(value, row, column) || null;
				} else if (checkIsExistValue() && column.template) {
					value = column.template(value, row, column);
				}
				checkIsExistValue() &&
					tooltip(value.toString(), {
						css: "dhx_grid_tooltip",
						node,
						htmlEnable: isHtmlEnable(this.config, column),
					});
			}
		};

		const showContentTootlip = (e, column) => {
			if (column && isTooltip(this.config, column)) {
				const value =
					(e.target.querySelector(".dhx_grid-header-cell-text span") &&
						e.target.querySelector(".dhx_grid-header-cell-text span").textContent) ||
					(e.target.querySelector(".dhx_grid-footer-cell-text span") &&
						e.target.querySelector(".dhx_grid-footer-cell-text span").textContent) ||
					"";
				value &&
					tooltip(value, {
						css: "dhx_grid_tooltip",
						node: e.target,
						htmlEnable: isHtmlEnable(this.config, column),
					});
			}
		};

		this._htmlEvents = {
			onclick: eventHandler(e => locate(e), {
				"dhx_grid-header-cell--sortable": (e, id) => {
					const isResizable = e.target.getAttribute("dhx_resized");
					const column = this._getColumn(id);
					if (
						column &&
						isSortable(this.config, column) &&
						!isResizable &&
						this.events.fire(GridEvents.beforeSort, [column, this._sortDir ? "asc" : "desc"])
					) {
						const text = locateNodeByClassName(e, "dhx_grid-header-cell")?.querySelector(
							".dhx_grid-header-cell-text_content"
						).innerHTML;
						const cellConfig = text ? column.header.find(item => item.text === text) : null;
						const dir = this._sortDir === "asc" && this._sortBy === id ? "desc" : "asc";
						this._sort(id, dir, cellConfig?.sortAs);
					}
				},
				"dhx_grid-expand-cell": (e, rowId) => {
					if (e.target.classList.contains("dhx_grid-expand-cell-icon")) {
						this.events.fire(GridEvents.expand, [rowId]);
					}
				},
				"dhx_span-expand-cell": (e, rowId) => {
					if (e.target.classList.contains("dhx_span-expand-cell-icon")) {
						this.events.fire(GridEvents.expand, [rowId]);
					}
				},
			}),
			onscroll: e => {
				this.events.fire(GridEvents.scroll, [
					{
						y: e.target.scrollTop,
						x: e.target.scrollLeft,
					},
				]);
			},
			onmouseover: {
				".dhx_grid-cell.dhx_boolean-cell .dhx_checkbox.dhx_cell-editor__checkbox": e => {
					const path = e.composedPath();
					const row = this.data.getItem(path[2].getAttribute("data-dhx-id"));
					const column = this._getColumn(path[1].getAttribute("data-dhx-col-id"));
					showCellTooltip(row, column, e.target);
				},
				".dhx_grid-cell:not(.dhx_boolean-cell)": e => {
					const row = this.data.getItem(e.composedPath()[1].getAttribute("data-dhx-id"));
					const column = this._getColumn(e.target.getAttribute("data-dhx-col-id"));
					showCellTooltip(row, column, e.target);
				},
				".dhx_grid-cell:not(.dhx_tree-cell) .dhx_grid-cell__content, .dhx_tree-cell :not(.dhx_grid-cell__content)": e => {
					const path = e.composedPath();
					const row = this.data.getItem(path[2].getAttribute("data-dhx-id"));
					const column = this._getColumn(path[1].getAttribute("data-dhx-col-id"));
					showCellTooltip(row, column, e.target);
				},
				".dhx_grid-cell.dhx_tree-cell .dhx_grid-cell__content": e => {
					const path = e.composedPath();
					const row = this.data.getItem(path[3].getAttribute("data-dhx-id"));
					const column = this._getColumn(path[2].getAttribute("data-dhx-col-id"));
					showCellTooltip(row, column, path[2]);
				},
				".dhx_span-cell:not(.dhx_grid-header-cell)": e => {
					const row = this.data.getItem(e.target.getAttribute("data-dhx-id"));
					const column = this._getColumn(e.target.getAttribute("data-dhx-col-id"));
					const span = this.getSpan(row.id, column.id);

					if (row && span && isTooltip(this.config, span)) {
						let value = span.text || toFormat(row[column.id], column.type, column.format);
						if (span.tooltipTemplate) {
							value = span.tooltipTemplate(value, span);
						} else if (column.template) {
							value = column.template(value, row, column);
						}

						value &&
							tooltip(value, {
								css: "dhx_grid_tooltip",
								node: e.target,
								htmlEnable: true,
							});
					}
				},
				".dhx_grid-header-cell:not(.dhx_span-cell)": e => {
					const column = this._getColumn(e.target.getAttribute("data-dhx-id"));
					showContentTootlip(e, column);
				},
				".dhx_grid-footer-cell:not(.dhx_span-cell)": e => {
					const column = this._getColumn(e.target.getAttribute("data-dhx-id"));
					showContentTootlip(e, column);
				},
				".dhx_grid-header-cell.dhx_span-cell": e => {
					const column = this._getColumn(e.target.getAttribute("data-dhx-id"));

					const headerSpan = column && column.header.find(item => !!(item.rowspan || item.colspan));

					if (column && headerSpan && isTooltip(this.config, column)) {
						const value = headerSpan.text || "";
						value &&
							tooltip(value, {
								css: "dhx_grid_tooltip",
								node: e.target,
								htmlEnable: isHtmlEnable(this.config, column),
							});
					}
				},
				".dhx_grid-header-cell-text_content": e => {
					const path = e.composedPath();
					const column = this._getColumn(path[1].getAttribute("data-dhx-id"));

					if (column && isTooltip(this.config, column)) {
						const value =
							(path[2].querySelector(".dhx_grid-header-cell-text_content") &&
								path[2].querySelector(".dhx_grid-header-cell-text_content").textContent) ||
							"";

						value &&
							tooltip(value, {
								css: "dhx_grid_tooltip",
								node: path[1],
								htmlEnable: isHtmlEnable(this.config, column),
							});
					}
				},
			},
		};

		if (this.config.dragMode || this.config.dragItem) {
			dragManager.setItem(this._uid, this);
			if (!this.config.dragItem) {
				this.config.dragItem = "row";
			}
			if (!this.config.dragMode) {
				this.config.dragMode = "both";
			}
		}

		this._init();

		if (this.config.columns) {
			this._parseColumns(true);
		}
		if (
			this.config.data &&
			this.config.data instanceof Array &&
			this.config.data.length &&
			this.config.columns
		) {
			this.data.parse(this.config.data);
		}

		this.selection = new Selection(
			this,
			{
				disabled: !this.config.selection,
			},
			this.events,
			this._uid
		);

		this.mount(container, this._createView());
		this.config.autoWidth && this.config.autoHeight && this._prepareData(this.config.data);

		awaitRedraw().then(() => {
			if (this.config.keyNavigation) {
				this.keyManager = new KeyManager((e: Event, focusId: Id) => {
					if (focusId === this._uid && this.events.fire(GridEvents.beforeKeyDown, [e])) {
						this.events.fire(GridEvents.afterKeyDown, [e]);
						return true;
					}
					return false;
				});
				this._initHotKey();
				focusManager.setFocusId(this._uid);
			}
		});

		if (config.autoEmptyRow && this.data.getLength() === 0) {
			this._addEmptyRow();
			this.paint();
		}
	}
	destructor() {
		this._destroyContent();
		this.keyManager && this.keyManager.destructor();
		this.events.events = {};
		this.events.context = null;
		this._activeFilters = this._filterData = this._scroll = this.content = null;
		this.unmount();
	}
	setColumns(columns: ICol[]) {
		this.config.columns = columns;
		this._parseColumns(true);
		this._adjustColumns();
		this._checkFilters();
		this._checkMarks();
		this.paint();
		if (this.config.keyNavigation) {
			awaitRedraw().then(() => {
				this._initHotKey(true);
				this.paint();
			});
		}
	}
	addRowCss(rowId: Id, css: string) {
		const item = this.data.getItem(rowId);
		const styles = item.$css || "";
		if (!styles.match(new RegExp(css, "g"))) {
			item.$css = styles + ` ${css}`;
			this.paint();
		}
	}
	removeRowCss(rowId: Id, css: string) {
		const item = this.data.getItem(rowId);
		const styles = item.$css ? item.$css.replace(css, "") : "";
		item.$css = styles;
		this.paint();
	}
	addCellCss(rowId: Id, colId: Id, css: string) {
		const column = this._getColumn(colId);
		const span = this.config.spans ? this.getSpan(rowId, colId) : null;
		if (span || column) {
			const cellStyle = span ? span.css : column.$cellCss[rowId];
			if (cellStyle) {
				// eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
				const newClass = cellStyle.match(new RegExp(css, "g")) ? "" : ` ${css}`;
				if (span) {
					span.css += newClass;
				} else {
					column.$cellCss[rowId] += newClass;
				}
			} else if (this.data.getItem(rowId)) {
				if (span) {
					span.css = `${css} `;
				} else {
					column.$cellCss[rowId] = `${css} `;
				}
			}
			this.paint();
		}
	}
	removeCellCss(rowId: Id, colId: Id, css: string) {
		const column = this._getColumn(colId);
		if (column) {
			if (column.$cellCss[rowId]) {
				column.$cellCss[rowId] = column.$cellCss[rowId].replace(css, "");
				this.paint();
			} else if (this.data.getItem(rowId)) {
				column.$cellCss[rowId] = "";
			}
		}
	}
	showColumn(colId: Id) {
		const column = this._getColumn(colId);
		if (!column || !column.hidden) {
			return;
		}

		if (!this.events.fire(GridEvents.beforeColumnShow, [column])) return;
		column.hidden = false;
		this.config.$totalWidth += column.$width;
		const filter = this._hiddenFilters && this._hiddenFilters[column.id];
		if (filter) {
			this._activeFilters[column.id] = filter;
			delete this._hiddenFilters[column.id];
		}
		this.paint();
		this._checkFilters();
		this.events.fire(GridEvents.afterColumnShow, [column]);
	}
	hideColumn(colId: Id) {
		const column = this._getColumn(colId);
		if (!column || column.hidden) {
			return;
		}

		if (!this.events.fire(GridEvents.beforeColumnHide, [column])) return;
		column.hidden = true;
		this.config.$totalWidth -= column.$width;
		const filter = this._activeFilters && this._activeFilters[column.id];
		if (filter) {
			if (!this._hiddenFilters) {
				this._hiddenFilters = {};
			}
			this._hiddenFilters[column.id] = filter;
			delete this._activeFilters[column.id];
			this.data.filter();
		}
		this.paint();
		const comboFilter = this.content.comboFilter;
		const afterPaint = () => {
			this._checkFilters();
			this.events.fire(GridEvents.afterColumnHide, [column]);
		};
		if (Object.keys(comboFilter.element).length) {
			awaitRedraw().then(() => {
				this.paint();
				afterPaint();
			});
		} else {
			afterPaint();
		}
	}
	isColumnHidden(colId: Id): boolean {
		const column = this._getColumn(colId);
		if (column) {
			return !!column.hidden;
		}
	}
	showRow(rowId: Id) {
		if (!isDefined(rowId)) {
			return;
		}
		const id = rowId.toString();
		const row = this.data.getItem(id);
		if (!row || !row.hidden) {
			return;
		}
		if (!this.events.fire(GridEvents.beforeRowShow, [row])) return;
		this.data.update(id, { hidden: false });
		this.data.filter(i => !i.hidden);
		this.events.fire(GridEvents.afterRowShow, [row]);
	}
	hideRow(rowId: Id) {
		if (!isDefined(rowId)) {
			return;
		}
		const id = rowId.toString();
		const row = this.data.getItem(id);
		if (!row) {
			return;
		}
		if (!this.events.fire(GridEvents.beforeRowHide, [row])) return;
		this.data.update(id, { hidden: true });
		this.data.filter(i => !i.hidden);
		this.events.fire(GridEvents.afterRowHide, [row]);
	}
	isRowHidden(rowId: Id): boolean {
		if (!isDefined(rowId)) {
			return;
		}
		const row = this.data.getItem(rowId.toString());
		if (row) {
			return !!row.hidden;
		}
	}
	getScrollState(): ICoords {
		return {
			x: this._scroll.left,
			y: this._scroll.top,
		};
	}
	scroll(x: number, y: number) {
		const gridBody =
			this.getRootView().refs.grid_body.el.querySelector(".scroll-view") ||
			this.getRootView().refs.grid_body.el;
		gridBody.scrollLeft = (x || x === 0) && typeof x === "number" ? x : gridBody.scrollLeft;
		gridBody.scrollTop = (y || y === 0) && typeof y === "number" ? y : gridBody.scrollTop;
		this.paint();
	}
	scrollTo(rowId: Id, colId: Id) {
		const selectedCell = this.selection.getCell();

		const columns = this.config.columns.filter(col => !col.hidden);
		const colInd = findIndex(columns, ({ id }) => id == colId);
		const prevCol = selectedCell ? selectedCell.column : this.config.columns[0];
		const prevColInd = findIndex(columns, obj => obj.id == prevCol.id);
		const fixedColsWidth = this.config.leftSplit
			? getTotalWidth(columns.slice(0, this.config.leftSplit))
			: 0;
		const x = getTotalWidth(columns.slice(0, colInd)) - (colInd - prevColInd < 0 ? fixedColsWidth : 0);

		const rows = this.data.getRawData(0, -1);
		const rowInd = findIndex(rows, ({ id }) => id == rowId);
		const y = getTotalHeight(rows.slice(0, rowInd));

		const scrollState = this.getScrollState();

		const gridRight = this.config.width + scrollState.x;
		const gridBottom =
			this.config.height + scrollState.y - this.config.headerRowHeight * this.config.$headerLevel;

		const cellTop = y - scrollState.y - rows[rowInd].$height;
		const cellLeft = x - scrollState.x - columns[colInd].$width;

		const cellBottom = y + rows[rowInd].$height * 2 + 18 - gridBottom;
		const cellRight = x + columns[colInd].$width * 2 + 18 - gridRight;

		const scrollTop = cellTop > 0 && cellBottom < 0 ? 0 : cellTop < 0 ? cellTop : cellBottom;
		const scrollLeft = cellLeft > 0 && cellRight < 0 ? 0 : cellLeft < 0 ? cellLeft : cellRight;

		this.scroll(scrollLeft + scrollState.x, scrollTop + scrollState.y);
	}
	adjustColumnWidth(colId: Id, adjust: IAdjustBy = true) {
		const columns = this.config.columns.filter(col => !col.hidden);
		const col = columns.filter(col => col.id === colId);
		const totalCols = this._adjustColumnsWidth(this.config.data, col, adjust);
		this.config.$totalWidth = columns.reduce((totalWidth, column) => {
			if (totalCols[column.id]) {
				column.$fixed = true;
				const max = column.maxWidth;
				const min = column.minWidth;
				const width = totalCols[column.id];
				column.$width = width;

				if (max && width > max) {
					column.$width = max;
				}
				if (min && column.$width < min) {
					column.$width = min;
				}
			}
			return (totalWidth += column.$width);
		}, 0);
		this.paint();
	}
	getCellRect(rowId: Id, colId: Id): ICellRect {
		const columns = this.config.columns.filter(col => !col.hidden);
		const rows = this.data.getRawData(0, -1);
		const span = this.getSpan(rowId, colId);

		const colInd = findIndex(columns, obj => obj.id == (span ? span.column : colId));
		const rowInd = findIndex(rows, obj => obj.id == (span ? span.row : rowId));

		const x = getTotalWidth(columns.slice(0, colInd));
		const y = getTotalHeight(rows.slice(0, rowInd));

		return {
			x,
			y,
			height: span?.rowspan
				? getHeight(rows, span.rowspan, rowInd)
				: rows[rowInd]
				? rows[rowInd].$height
				: 0,
			width: span?.colspan
				? getWidth(columns, span.colspan, colInd)
				: columns[colInd]
				? columns[colInd].$width
				: 0,
		};
	}
	getColumn(colId: Id): ICol {
		const id = findIndex(this.config.columns, col => col.id == colId);
		if (id >= 0) {
			return this.config.columns[id];
		}
	}
	addSpan(spanObj: ISpan) {
		this.config.spans = this.config.spans || [];
		const index = findIndex(
			this.config.spans,
			span => `${span.row}` === `${spanObj.row}` && `${span.column}` === `${spanObj.column}`
		);
		if (index >= 0) {
			this.config.spans[index] = spanObj;
			return;
		}
		this.config.spans.push(normalizeSpan(spanObj, this.config));
		this._checkMarks();
		this.paint();
	}
	getSpan(rowId: Id, colId: Id): ISpan {
		if (this.config.spans) {
			const rowIndex = this.data.getIndex(rowId.toString());
			const colIndex = this.config.columns.findIndex(col => col.id === colId);

			const index = findIndex(this.config.spans, span => {
				const startRowInd = this.data.getIndex(span.row.toString());
				const startColInd = this.config.columns.findIndex(col => col.id === span.column);
				const rows = {
					start: startRowInd,
					end: span.rowspan ? startRowInd + span.rowspan : startRowInd + 1,
				};
				const cols = {
					start: startColInd,
					end: span.colspan ? startColInd + span.colspan : startColInd + 1,
				};
				return (
					rowIndex >= rows.start &&
					rowIndex < rows.end &&
					colIndex >= cols.start &&
					colIndex < cols.end
				);
			});

			return this.config?.spans[index];
		}
	}
	removeSpan(rowId: Id, colId: Id) {
		if (this.config.spans) {
			const index = findIndex(
				this.config.spans,
				span => `${span.row}` == `${rowId}` && `${span.column}` == `${colId}`
			);
			this.config.spans.splice(index, 1);
			this.paint();
		}
	}
	editCell(rowId: Id, colId: Id, editorType?: EditorType): void {
		const row = this.data.getItem(rowId);
		const col = this.getColumn(colId);
		if (!row || !col) {
			dhxWarning("item not found");
			return;
		}
		const colEditorType = col.editorType;
		if (!editorType) {
			if (col.type === "date") {
				editorType = "datePicker";
			}
			if (col.type === "boolean") {
				editorType = "checkbox";
			}
			if (colEditorType) {
				editorType = colEditorType;
			}
		}
		if (!this.events.fire(GridEvents.beforeEditStart, [row, col, editorType])) {
			return;
		}
		if (
			this.config.$editable?.editor ||
			(this.config.$editable &&
				this.config.$editable.row === rowId &&
				this.config.$editable.col === colId &&
				this.config.$editable.editorType === editorType)
		) {
			return;
		}
		this.config.$editable = {
			row: row.id,
			col: col.id,
			isSpan: !!this.getSpan(row.id, col.id),
			editorType,
		};
		if (!this.selection.config.disabled) {
			this.selection.setCell(rowId.toString(), colId.toString());
		}
		this.paint();
		this.events.fire(GridEvents.afterEditStart, [row, col, editorType]);
	}
	editEnd(withoutSave?: boolean) {
		if (this.config.$editable && this.config.$editable.editor) {
			this.config.$editable.editor.endEdit(withoutSave);
		}
	}
	getSortingState(): ISortingState {
		return { dir: this._sortDir, by: this._sortBy };
	}
	getHeaderFilter(colId: Id): HTMLElement | Combobox {
		const col = this.getColumn(colId);
		if (!col) {
			return;
		}
		let filter = null;
		col.header.forEach(cell => {
			if (cell.content) {
				const filterEl = this.content[cell.content].element[colId];
				filter =
					cell.content === "comboFilter" ? filterEl : this.getRootView().refs[`${colId}_filter`].el;
			}
		});
		return filter;
	}
	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	edit(rowId: Id, colId: Id, editorType?: EditorType): void {
		this.editCell(rowId, colId, editorType);
	}
	paint() {
		const customHotkeysLength = this.config.hotkeys ? Object.keys(this.config.hotkeys).length : 0;
		if (this.keyManager && !(this.keyManager.getKeyStorageLength() - customHotkeysLength)) {
			this._initHotKey(true);
		}
		super.paint();
	}
	protected _createView() {
		return create(
			{
				render: (vm, obj) => {
					return render(vm, obj, this._htmlEvents, this.selection, this._uid);
				},
			},
			this
		);
	}
	protected _parseColumns(configChanged = false) {
		normalizeColumns(this.config, configChanged);
		const columns = this.config.columns.filter(col => !col.hidden);
		countColumns(this.config, columns);
	}
	protected _parseData() {
		this.config.data = this._prepareData(this.data);
		this._detectColsTypes();
		this._checkFilters();
		this._checkMarks();
		this.data.filter(i => !i.hidden);
		this._render();
	}
	protected _createCollection(prep: (data: any[]) => any[]) {
		this.data = new DataCollection({ prep }, this.events);
	}
	protected _getRowIndex(rowId: Id): number {
		return this.data.getIndex(rowId);
	}
	protected _setEventHandlers() {
		const updater = (updateObj: any) => ({ source, target }) => {
			if (source && source instanceof Array && source.length > 1) {
				source.map(
					selectedId => this.data.exists(selectedId) && this.data.update(selectedId, updateObj)
				);
				return;
			}
			if (this.data.exists(target)) {
				this.data.update(target, updateObj);
			}
		};
		this.data.events.on(DataEvents.load, () => {
			this.data.filter(i => i);
			this._parseData();
			this._checkFilters();
		});
		this.data.events.on(DataEvents.change, (id, status, obj) => {
			if (status === "setPage") {
				awaitRedraw().then(() => {
					const colVisible = this.config.columns.find(col => col.hidden !== true);
					this.scrollTo(this.data.getId(obj[0]).toString(), colVisible.id.toString());
					this._render();
				});
				return;
			}
			if (status === "add" || status === "update" || status === "remove") {
				if (id && status === "remove") {
					const removed = this.selection.getCells().find(cell => cell.row.id === id);
					removed && this.selection.removeCell(removed.row.id, removed.column.id);
				}
				this.config.data = this._prepareData(this.data);
			} else {
				if (isTreeCollection(this.data)) {
					this.config.data = this._prepareData(this.data);
				}
				this._adjustColumns();
			}
			if (id) {
				this._filterData = this.data.map(el => el) || [];
				this._checkFilters();
			}
			this._detectColsTypes();
			this._removeMarks();
			this._checkMarks();
			if (this.config.autoEmptyRow && (!this._activeFilters || isEmptyObj(this._activeFilters))) {
				const emptyRow = this.data.find({ by: "$emptyRow", match: true });
				if (emptyRow) {
					if (emptyRow.id === id) return;
					this.data.move(emptyRow.id, this.data.getLength() - 1);
				} else {
					this._addEmptyRow();
				}
			}
			this._render();
		});
		this.data.events.on(DataEvents.removeAll, () => {
			this.config.columns.map(col => {
				col.header.map(cell => {
					if (cell.content && (cell.content === "selectFilter" || cell.content === "comboFilter")) {
						col.$uniqueData = [];
					}
				});
			});
		});

		this.events.on(DragEvents.beforeDrag, (data, events) => {
			if (this.data.getItem(data.start)) {
				return this.events.fire(GridEvents.beforeRowDrag, [data, events]);
			} else if (this.config.dragItem === "column" || this.config.dragItem === "both") {
				return this.events.fire(GridEvents.beforeColumnDrag, [data, events]);
			}
		});
		this.events.on(DragEvents.dragStart, (data, events) => {
			updater({ $dragtarget: true });
			if (this.data.getItem(data.start)) {
				this.events.fire(GridEvents.dragRowStart, [data, events]);
			} else if (this.config.dragItem === "column" || this.config.dragItem === "both") {
				this.events.fire(GridEvents.dragColumnStart, [data, events]);
			}
		});
		this.events.on(DragEvents.dragIn, (data, events) => {
			if (this.data.getItem(data.start)) {
				this.events.fire(GridEvents.dragRowIn, [data, events]);
			} else if (this.config.dragItem === "column" || this.config.dragItem === "both") {
				this.events.fire(GridEvents.dragColumnIn, [data, events]);
			}
		});
		this.events.on(DragEvents.dragOut, (data, events) => {
			if (this.data.getItem(data.start)) {
				this.events.fire(GridEvents.dragRowOut, [data, events]);
			} else if (this.config.dragItem === "column" || this.config.dragItem === "both") {
				this.events.fire(GridEvents.dragColumnOut, [data, events]);
			}
		});
		this.events.on(DragEvents.canDrop, (data, events) => {
			updater({ $drophere: true });
			if (this.data.getItem(data.start) || this.data.getItem(data.target)) {
				this.events.fire(GridEvents.canRowDrop, [data, events]);
			} else if (this.config.dragItem === "column" || this.config.dragItem === "both") {
				this.events.fire(GridEvents.canColumnDrop, [data, events]);
			}
		});
		this.events.on(DragEvents.cancelDrop, (data, events) => {
			updater({ $drophere: undefined });
			if (this.data.getItem(data.start)) {
				this.events.fire(GridEvents.cancelRowDrop, [data, events]);
			} else if (this.config.dragItem === "column" || this.config.dragItem === "both") {
				this.events.fire(GridEvents.cancelColumnDrop, [data, events]);
			}
		});
		this.events.on(DragEvents.beforeDrop, (data, events) => {
			if (
				this.config.dragItem === "row" &&
				(this.config.dragMode === "both" || this.config.dragMode === "target")
			) {
				return this.events.fire(GridEvents.beforeRowDrop, [data, events]);
			} else if (this.config.dragItem === "column" || this.config.dragItem === "both") {
				return this.events.fire(GridEvents.beforeColumnDrop, [data, events]);
			}
		});
		this.events.on(DragEvents.afterDrop, (data, events) => {
			if (this.data.getItem(data.start)) {
				this.events.fire(GridEvents.afterRowDrop, [data, events]);
				const item = this.data.getItem(data.start);
				for (const col of this.config.columns) {
					if (typeof item[col.id] === "undefined") {
						this.data.update(item.id, { [col.id]: null }, true);
					}
				}
				const initData = this.data.getInitialData();
				const startIndex = initData?.findIndex(item => item.id.toString() === data.start);
				const targetIndex = initData?.findIndex(item => item.id.toString() === data.target);
				if (startIndex > -1 && targetIndex > -1)
					initData?.splice(targetIndex, 0, initData?.splice(startIndex, 1)[0]);
				this.config.data = this._prepareData(initData || this.data.map(i => i));
				this.data.parse(this.config.data);
				for (const compare in this._activeFilters) {
					this.data.filter(this._activeFilters[compare], { add: true });
				}
			} else if (this.config.dragItem === "column" || this.config.dragItem === "both") {
				this.events.fire(GridEvents.afterColumnDrop, [data, events]);
			}
		});
		this.events.on(DragEvents.afterDrag, (data, events) => {
			updater({ $dragtarget: undefined });
			if (this.data.getItem(data.start)) {
				this.events.fire(GridEvents.afterRowDrag, [data, events]);
			} else if (this.config.dragItem === "column" || this.config.dragItem === "both") {
				this.events.fire(GridEvents.afterColumnDrag, [data, events]);
			}
			this.config.data = this._prepareData(
				this.data instanceof Array ? this.data.map(i => i) : this.data.getInitialData() || this.data
			);
			this.data.parse(this.config.data);
		});

		// TODO: When introducing touch events, remove system events
		this.events.on(GridEvents.cellMouseDown, (row: IRow, col: ICol, e: MouseEvent & TouchEvent) => {
			if (!e.targetTouches) {
				this._dragStart(e);
			} else {
				this._touch.timer = setTimeout(() => {
					this._dragStart(e);
				}, this._touch.duration);

				if (!this._touch.timeStamp) {
					this._touch.timeStamp = +e.timeStamp.toFixed();
				} else {
					const doubleTap =
						this._touch.dblDuration >= this._touch.timeStamp - +e.timeStamp.toFixed();
					if (doubleTap) {
						if ((col.editable !== false && this.config.editable) || col.editable) {
							this.editCell(row.id, col.id, col.editorType);
						}
						e.preventDefault();
						// to simulate on touch devices
						this.events.fire(GridEvents.cellDblClick, [row, col, e]);
					}
					this._touch.timeStamp = null;
				}
				setTimeout(() => {
					this._touch.timeStamp = null;
				}, this._touch.dblDuration);
			}
		});
		this._events.on(GridSystemEvents.cellTouchMove, (_row: IRow, _col: ICol, e: TouchEvent) => {
			this._touch.start && e.preventDefault();
			this._clearTouchTimer();
		});
		this._events.on(GridSystemEvents.cellTouchEnd, () => {
			this._touch.start = false;
			this._clearTouchTimer();
		});
		this.events.on(GridEvents.filterChange, (val: any, colId: Id, filter: string) => {
			val = val ?? "";
			if (this.config.autoEmptyRow) {
				const emptyRow = this.data.find({ by: "$emptyRow", match: true });
				if (emptyRow) {
					this.data.remove(emptyRow.id);
				}
			}
			if (!this._activeFilters) {
				this._activeFilters = {};
			}
			const columnConfig = this._getColumn(colId);
			const conf = columnConfig.header.filter(
				item => item.content === filter && item.customFilter !== undefined
			)[0];

			if (val !== "") {
				if (
					(columnConfig.editorType === "combobox" ||
						columnConfig.editorType === "select" ||
						columnConfig.editorType === "multiselect") &&
					columnConfig.options
				) {
					if (Array.isArray(val)) {
						val = val.map(item => {
							return (
								columnConfig.options.find(option =>
									typeof option === "string" ? option === item : option.value === item
								)?.id || item
							);
						});
					} else {
						val =
							columnConfig.options.find(option =>
								typeof option === "string" ? option === val : option.value === val
							)?.id ?? val;
					}
				}
				const match = colId => {
					const col = this._getColumn(colId);
					return (val, match, obj, multi) => {
						return this.content[filter].match({ val, match, obj, multi, col });
					};
				};
				this._activeFilters[colId] = {
					by: colId,
					match: val,
					compare: conf?.customFilter ?? match(colId),
					multi: columnConfig?.editorType === "multiselect",
				};
			} else {
				delete this._activeFilters[colId];
			}
			this.data.filter(i => !i.hidden);
			for (const compare in this._activeFilters) {
				this.data.filter(this._activeFilters[compare], { add: true });
			}
		});
		this.events.on(GridEvents.scroll, scrollState => {
			this._scroll = { top: scrollState.y, left: scrollState.x };
			this.editEnd();
			this.paint();
		});
		this.events.on(GridEvents.cellDblClick, (row, col, e) => {
			const targetCheckbox = locateNodeByClassName(e, "dhx_boolean-cell");
			if (targetCheckbox) return;
			if ((col.editable !== false && this.config.editable) || col.editable) {
				this.editCell(row.id, col.id, col.editorType);
			}
		});
		this.events.on(GridEvents.afterEditEnd, (value, eRow, eCol) => {
			if (
				this.config.$editable?.editor &&
				(this.config.$editable.col !== eCol.id || this.config.$editable.row !== eRow.id)
			)
				return;

			let row;
			let col;
			if (!this.config.$editable) {
				row = eRow.id;
				col = eCol.id;
			} else {
				row = this.config.$editable.row;
				col = this.config.$editable.col;
			}
			const span = this.getSpan(row, col);
			if (span && typeof span.text === "string") {
				span.text = value as string;
			} else {
				const item = this.data.getItem(row);
				delete item.$emptyRow;
				if (value !== undefined) {
					this.data.update(row, { ...item, [col]: value });
				}
			}
			this.config.$editable = null;
			this._checkFilters();
			this.paint();
		});
		this.events.on(GridEvents.headerCellMouseDown, (col: ICol, e: MouseEvent & TouchEvent) => {
			const target = e.target as HTMLElement;
			const resizedColumn = target.getAttribute("dhx_resized");

			if (resizedColumn && this.events.fire(GridEvents.beforeResizeStart, [col, e])) {
				startResize(this, resizedColumn.toString(), e, () => {
					this.paint();
					this.config.$resizing = null;
					this.events.fire(GridEvents.afterResizeEnd, [col, e]);
				});
			}
			if (e.targetTouches) {
				if (!this._touch.timeStamp) {
					this._touch.timeStamp = +e.timeStamp.toFixed();
				} else {
					const doubleTap =
						this._touch.dblDuration >= this._touch.timeStamp - +e.timeStamp.toFixed();
					if (doubleTap) {
						e.preventDefault();
						// to simulate on touch devices
						this.events.fire(GridEvents.headerCellDblClick, [col, e]);
					}
					this._touch.timeStamp = null;
				}
				setTimeout(() => {
					this._touch.timeStamp = null;
				}, this._touch.dblDuration);
			}
		});
		this.events.on(GridEvents.footerCellDblClick, (col: ICol, e: any) => {
			if (e.targetTouches) {
				if (!this._touch.timeStamp) {
					this._touch.timeStamp = +e.timeStamp.toFixed();
				} else {
					const doubleTap =
						this._touch.dblDuration >= this._touch.timeStamp - +e.timeStamp.toFixed();
					if (doubleTap) {
						e.preventDefault();
						// to simulate on touch devices
						this.events.fire(GridEvents.footerCellDblClick, [col, e]);
					}
					this._touch.timeStamp = null;
				}
				setTimeout(() => {
					this._touch.timeStamp = null;
				}, this._touch.dblDuration);
			}
		});
		this.events.on(GridEvents.resize, () => {
			this._parseColumns();
			this._checkFilters();
		});
		this.events.on(GridEvents.afterResizeEnd, col => {
			this.config.columns = this.config.columns?.map(c => {
				if (c.id == col.id) c.width = col.$width;
				return c;
			});
			this._parseColumns(true);
			this._checkFilters();
		});
	}
	protected _addEmptyRow() {
		const id = this.data.getId(this.data.getLength() - 1);
		const lastRow = this.data.getItem(id);
		const columns = this.config.columns.filter(col => !col.hidden);

		const isEmpty = isRowEmpty(lastRow);

		if (!isEmpty) {
			this.data.add(
				columns.reduce(
					(total, col) => {
						total[col.id] = "";
						return total;
					},
					{ $emptyRow: true }
				)
			);
		}
	}
	protected _sort(by: Id, dir?: Dirs, sortAs?: SortFunction) {
		if (!dir) {
			if (this._sortBy === by) {
				this._sortDir = this._sortDir === "asc" ? "desc" : "asc";
			} else {
				this._sortDir = "asc";
			}
		} else {
			this._sortDir = dir;
		}
		const defaultAs = item => {
			const col = this.getColumn(by);
			if (item && col.type === "date") {
				return typeof item === "string" ? stringToDate(item, col.format).getTime() : item.getTime();
			}
			return item ? `${item}` : "";
		};
		this._sortBy = by;
		this.data.sort({
			by,
			dir: this._sortDir,
			as: sortAs ?? defaultAs,
		});
		this.events.fire(GridEvents.afterSort, [this.getColumn(by), this._sortDir]);
	}
	protected _clearTouchTimer(): void {
		if (this._touch.timer) {
			clearTimeout(this._touch.timer);
			this._touch.timer = null;
		}
	}
	protected _checkFilters() {
		const data = this._filterData;
		if (!data) return;
		this.config.columns.forEach(col => {
			col.header.forEach(cell => {
				if (cell.content && (cell.content === "selectFilter" || cell.content === "comboFilter")) {
					const headerWithFilterConfig = col.header.find(i => i.filterConfig);
					const unique = [];
					if (cell.content === "comboFilter" && cell.filterConfig?.readonly) unique.push("");
					unique.push(
						...getUnique(
							data,
							col.id as string,
							headerWithFilterConfig ? headerWithFilterConfig.filterConfig.multiselection : null
						)
					);
					if (col.$uniqueData && col.$uniqueData.length > unique.length) {
						unique.forEach(item => {
							if (!col.$uniqueData.includes(item)) {
								col.$uniqueData.push(item);
							}
						});
					} else {
						col.$uniqueData = unique;
					}
				}
			});
		});
		for (const compare in this._activeFilters) {
			const col = this.config.columns.find(i => i.id === compare);
			const filter = col.header.find(i => !!i.content);
			let exist = false;
			if (Array.isArray(this._activeFilters[compare].match)) {
				exist = this._activeFilters[compare].match.reduce((_, i) => {
					if (col.$uniqueData.find(j => j.toString() === i)) return true;
				}, false);
			} else {
				exist = col.$uniqueData.find(i => i.toString() === this._activeFilters[compare].match);
			}
			if (filter && (filter.content === "selectFilter" || filter.content === "comboFilter") && !exist) {
				delete this._activeFilters[compare];
				this.data.filter();
			} else {
				this.data.filter(this._activeFilters[compare], { add: true });
			}
		}
	}
	protected _adjustColumns() {
		if (
			typeof this.config.adjust === "boolean" ||
			this.config.adjust === "data" ||
			this.config.adjust === "header" ||
			this.config.adjust === "footer"
		) {
			const columns = this.config.columns.filter(col => !col.hidden);
			if (!columns.length) return;
			const data =
				!this.config.data || !this.config.data.length ? this.data.map(row => row) : this.config.data;
			const totalCols = this._adjustColumnsWidth(data, columns);
			this.config.$totalWidth = columns.reduce((totalWidth, column) => {
				column.$fixed = true;
				const max = column.maxWidth;
				const min = column.minWidth;
				const width = totalCols[column.id];
				column.$width = width;

				if (max && width > max) {
					column.$width = max;
				}
				if (min && column.$width < min) {
					column.$width = min;
				}
				return (totalWidth += column.$width);
			}, 0);
		} else {
			const columns = this.config.columns.filter(col => !col.hidden && col.adjust);
			if (!columns.length) return;
			columns.forEach(col => {
				this.adjustColumnWidth(col.id, col.adjust);
			});
		}
	}
	protected _prepareData(data: IDataItem[] | IDataCollection) {
		if (this.config.autoHeight) this.config.autoHeight = false;

		this._adjustColumns();

		return data.map(row => {
			row.$height = row.height || this.config.rowHeight;
			return row;
		});
	}
	protected _adjustColumnsWidth(
		rows: IRow[],
		cols: ICol[],
		adjust: IAdjustBy = this.config.adjust
	): IColumnsWidth {
		let columnsWidth = {};
		if (adjust === "header" || adjust === true) {
			const currentCols = cols.filter(col => col.header);
			const data = getMaxColsWidth(
				this._prepareColumnData(currentCols, "header"),
				currentCols,
				{
					font: "bold 14.4px Arial",
				},
				"header"
			);
			if (data) {
				for (const [key, value] of Object.entries(data)) {
					columnsWidth = Object.assign(columnsWidth, {
						[key]: +value + (isSortable(this.config, this.getColumn(key)) ? 36 : 16),
					});
				}
			}
		}

		if (adjust === "footer" || adjust === true) {
			const currentCols = cols.filter(col => col.footer);
			const data = getMaxColsWidth(
				this._prepareColumnData(currentCols, "footer"),
				currentCols,
				{
					font: "bold 14.4px Arial",
				},
				"footer"
			);
			if (data) {
				for (const [key, value] of Object.entries(data)) {
					if ((columnsWidth[key] && columnsWidth[key] < +value + 16) || !columnsWidth[key]) {
						columnsWidth = Object.assign(columnsWidth, {
							[key]: +value + 16,
						});
					}
				}
			}
		}

		if (adjust === "data" || adjust === true) {
			const data = getMaxColsWidth(
				rows,
				cols,
				{
					font: "normal 14.4px Arial",
				},
				"data"
			);
			if (data) {
				for (const [key, value] of Object.entries(data)) {
					if ((columnsWidth[key] && columnsWidth[key] < +value + 16) || !columnsWidth[key]) {
						columnsWidth = Object.assign(columnsWidth, {
							[key]: +value + 16,
						});
					}
				}
			}
		}

		return columnsWidth;
	}
	protected _prepareColumnData(data, type: "header" | "footer"): IRow[] {
		const totalRow = [];
		for (let i = 0; i < data.length; i++) {
			const row = [];
			for (let k = 0; k < data[i]?.[type].length; k++) {
				const rowData = {};
				if (data[i]?.[type][k]?.text) {
					rowData[data[i].id] = data[i]?.[type][k]?.text || "";
					row.push(rowData);
				} else if (data[i]?.[type][k]?.content) {
					rowData[data[i].id] =
						this.content[data[i]?.[type][k]?.content].toHtml(
							this.getColumn(data[i].id),
							this.config as any
						) || "";
					row.push(rowData);
				}
			}
			for (let j = 0; j < row.length; j++) {
				for (const [key, value] of Object.entries(row[j])) {
					totalRow[j] = { ...totalRow[j] } || {};
					totalRow[j] = Object.assign(totalRow[j], { [key]: value });
				}
			}
		}
		return totalRow;
	}
	private _dragStart(e: any): void {
		if (
			this.config.dragMode &&
			(this.config.dragItem === "row" || this.config.dragItem === "both") &&
			!this.config.$editable
		) {
			const column = this._getColumn(e.target.getAttribute("data-dhx-col-id"));
			if (column?.draggable === false) return;
			const item = locateNode(e, "data-dhx-id");
			const itemId = item && item.getAttribute("data-dhx-id");
			if (e.targetTouches) {
				this._touch.start = true;
			}
			return dragManager.onMouseDown(e, [itemId]);
		}
	}
	private _getColumn(colId: Id): ICol {
		for (const col of this.config.columns) {
			if (col.id == colId) {
				return col;
			}
		}
	}
	private _init() {
		this.events = new EventSystem(this);
		this._events = new EventSystem(this);
		this._attachDataCollection();
		this.export = new Exporter(this);
		this._setEventHandlers();
	}
	private _attachDataCollection() {
		const prep = data => {
			if (data.spans) {
				this.config.spans = data.spans;
				data = data.data;
			}
			return data;
		};
		if (this.config.data instanceof DataCollection) {
			this.data = this.config.data;
			this.config.data = this.data.serialize();
			return;
		}
		this._createCollection(prep);
	}
	private _setMarks(col: ICol, func: any) {
		const colCells = this.data.map(row => ({
			id: row.id,
			data: row[col.id],
			row,
		}));
		const colCellsData = this.data.map(row => row[col.id]);
		for (const cell of colCells) {
			const css = func(cell.data, colCellsData, cell.row, col);
			if (css) {
				col.$cellCss = col.$cellCss || {};
				const cellCss = (col.$cellCss[cell.id] || "").split(" ");
				css.split(" ").map(item => {
					if (!cellCss.includes(item)) {
						cellCss.push(item);
					}
				});
				const cellCssStr = cellCss.join(" ");
				const span = this.getSpan(cell.id, col.id);
				col.$cellCss[cell.id] = cellCssStr;
				if (
					span &&
					(span.rowspan || (span.colspan && span.column === col.id)) &&
					span.$markCss !== cellCssStr
				) {
					span.$markCss = cellCssStr;
				}
			}
		}
	}
	private _checkMarks() {
		this.config.columns.map(col => {
			const mark = col.mark;
			if (mark) {
				if (typeof mark === "function") {
					this._setMarks(col, mark);
				} else {
					this._setMarks(col, (el, c) => {
						const data = [];
						c.forEach(item => {
							if (item !== null && item !== undefined && item !== "") {
								data.push(parseFloat(item));
							}
						});
						const min = getMinArrayNymber(data);
						const max = getMaxArrayNymber(data);
						if (mark.max && max === parseFloat(el)) {
							return mark.max;
						}
						if (mark.min && min === parseFloat(el)) {
							return mark.min;
						}
						return false;
					});
				}
			}
		});
	}
	private _removeMarks() {
		this.config.columns.forEach(col => {
			if (col.mark) {
				col.$cellCss = {};
			}
		});
		this.config.spans?.forEach(span => {
			if (span.$markCss) {
				delete span.$markCss;
			}
		});
	}
	// [todo] make more smart type detection
	private _detectColsTypes() {
		this.config.columns.forEach(col => {
			if (col.type) {
				return col;
			}
			if (col.format) {
				col.type = "number";
				return col;
			}
			if (!col.type) {
				col.type = "string";
			}
		});
	}
	private _destroyContent() {
		for (const contentName in this.content) {
			if (contentName === "comboFilter") {
				this.content[contentName].destroy();
			}
		}
	}
	private _render() {
		this.paint();
	}
	private _initHotKey(secondInit = false) {
		const handlers = getKeysHandlers(this);

		for (const key in handlers) {
			if (!this.keyManager.exist(key)) this.keyManager.addHotKey(key, handlers[key]);
		}

		if (!secondInit) {
			for (const key in this.config.hotkeys) {
				this.keyManager.addHotKey(key, this.config.hotkeys[key]);
			}
		}
	}
}
